import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSelectionDiamonds,
  setPage, setPageSize,
  selectFilteredDiamonds,
} from '../../redux/slices/selectionSlice';
import {
  Dialog, DialogTitle, DialogContent, IconButton,
} from '@mui/material';
import { Close, BarChart } from '@mui/icons-material';
import FilterPanel from './FilterPanel';
import SupplierSidebar from './SupplierSidebar';
import SelectionBreakdown from './SelectionBreakdown';
import DiamondCard from './DiamondCard';
import DiamondTable from './DiamondTable';
import './Selection.css';

const Selection = ({ accountDetails }) => {
  const dispatch = useDispatch();
  const { view, page, pageSize, isLoading, error } = useSelector(s => s.selection);
  const filtered = useSelector(selectFilteredDiamonds);

  const [breakdownOpen, setBreakdownOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchSelectionDiamonds());
  }, [dispatch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const start      = (page - 1) * pageSize;
  const paginated  = filtered.slice(start, start + pageSize);

  const totalCt    = filtered.reduce((s, d) => s + parseFloat(d.Carats        || 0), 0);
  const totalPrice = filtered.reduce((s, d) => s + parseFloat(d['Total Price'] || 0), 0);

  return (
    <div className="sel-page">
      {/* ── Stats bar ── */}
      <div className="sel-stats-bar">
        <div className="sel-stat-block">
          <span className="sel-stat-label">Count</span>
          <span className="sel-stat-value">{filtered.length}</span>
        </div>
        <div className="sel-stat-block">
          <span className="sel-stat-label">Total Carat</span>
          <span className="sel-stat-value">{totalCt.toFixed(2)}</span>
        </div>
        <div className="sel-stat-block">
          <span className="sel-stat-label">Total Price</span>
          <span className="sel-stat-value">
            ${totalPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </span>
        </div>

        <div className="sel-stats-right">
          {/* Pagination */}
          <div className="sel-pagination-row">
            <span className="sel-per-page-label">Per page:</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 60, fontSize: 12 }}
              value={pageSize}
              onChange={e => dispatch(setPageSize(e.target.value))}
            >
              <option value="15">15</option>
              <option value="30">30</option>
              <option value="60">60</option>
            </select>
            <button
              className="sel-page-btn"
              disabled={page === 1}
              onClick={() => dispatch(setPage(page - 1))}
            >‹</button>
            <span className="sel-page-info">{page} / {totalPages}</span>
            <button
              className="sel-page-btn"
              disabled={page >= totalPages}
              onClick={() => dispatch(setPage(page + 1))}
            >›</button>
            <span className="sel-count-info">{filtered.length} shown</span>
          </div>

          {/* Breakdown button */}
          <button
            className="btn btn-sm btn-outline-primary"
            style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}
            onClick={() => setBreakdownOpen(true)}
          >
            <BarChart style={{ fontSize: 16 }} />
            Breakdown
          </button>
        </div>
      </div>

      {/* ── Filter panel ── */}
      <FilterPanel />

      {/* ── Main layout: sidebar + content ── */}
      <div className="sel-diamonds-layout">
        <SupplierSidebar />

        <div className="sel-main-view">
          {isLoading ? (
            <div className="sel-loader">
              <div className="spinner-border text-primary" role="status" />
              <span style={{ marginLeft: 12, color: '#666', fontSize: 13 }}>
                Loading selection data…
              </span>
            </div>
          ) : error ? (
            <div className="sel-empty">
              <div className="sel-empty-icon">⚠️</div>
              <div className="sel-empty-text" style={{ color: '#dc3545' }}>
                Failed to load: {error}
              </div>
              <button
                className="btn btn-sm btn-outline-primary mt-3"
                onClick={() => dispatch(fetchSelectionDiamonds())}
              >
                Retry
              </button>
            </div>
          ) : paginated.length === 0 ? (
            <div className="sel-empty">
              <div className="sel-empty-icon">💎</div>
              <div className="sel-empty-text">No diamonds match the current filters.</div>
            </div>
          ) : view === 'card' ? (
            <div className="row">
              {paginated.map(d => (
                <DiamondCard key={d['LD ID']} diamond={d} accountDetails={accountDetails} />
              ))}
            </div>
          ) : (
            <DiamondTable diamonds={paginated} />
          )}
        </div>
      </div>

      {/* ── Breakdown modal ── */}
      <Dialog
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { maxHeight: '80vh' } }}
      >
        <DialogTitle style={{ padding: '12px 16px', fontSize: 15, fontWeight: 600 }}>
          Selection Breakdown
          <IconButton
            onClick={() => setBreakdownOpen(false)}
            size="small"
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: '8px 16px' }}>
          <SelectionBreakdown />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Selection;
